import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
  Tooltip,
  IconButton,
  Collapse
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Description as TemplateIcon,
  Schedule as ScheduleIcon,
  Email as EmailIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Apps as AppsIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ open, onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [settingsOpen, setSettingsOpen] = React.useState(false);

  const menuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/',
      adminOnly: true
    },
    {
      text: 'Minhas Assinaturas',
      icon: <EmailIcon />,
      path: '/my-signatures',
      adminOnly: false
    }
  ];

  const settingsItems = [
    {
      text: 'Usuários',
      icon: <PeopleIcon />,
      path: '/users',
      adminOnly: true
    },
    {
      text: 'Templates',
      icon: <TemplateIcon />,
      path: '/templates',
      adminOnly: true
    },
    {
      text: 'Aplicações',
      icon: <AppsIcon />,
      path: '/app-config',
      adminOnly: true
    },
    {
      text: 'Agendadores',
      icon: <ScheduleIcon />,
      path: '/schedulers',
      adminOnly: true
    }
  ];

  const handleNavigation = (path: string) => {
    // Se o usuário não é admin e tenta acessar o dashboard, redireciona para minhas assinaturas
    if (!user?.isAdmin && path === '/') {
      navigate('/my-signatures');
    } else {
      navigate(path);
    }
    // Removido o onClose() para que o menu não feche automaticamente
  };

  const handleSettingsToggle = () => {
    setSettingsOpen(!settingsOpen);
  };

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
             <style>
         {`
           .MuiTouchRipple-root {
             display: none !important;
             opacity: 0 !important;
             visibility: hidden !important;
           }
                       .MuiListItem-root {
              padding: 0 !important;
              margin: 0 !important;
              border: none !important;
              outline: none !important;
              box-shadow: none !important;
              background: none !important;
              background-color: transparent !important;
            }
            .css-141oohx-MuiListItem-root {
              padding: 0 !important;
              margin: 0 !important;
              border: none !important;
              outline: none !important;
              box-shadow: none !important;
              background: none !important;
              background-color: transparent !important;
            }
         `}
       </style>
      <Drawer
      variant="persistent"
      anchor="left"
      open={open}
      sx={{
        width: 280,
        flexShrink: 0,
        zIndex: (theme) => theme.zIndex.drawer,
        '& .MuiDrawer-paper': {
          width: 280,
          boxSizing: 'border-box',
          borderRight: '1px solid #e2e8f0',
          backgroundColor: '#ffffff',
          top: '64px', // Posicionar abaixo da navbar
          height: 'calc(100vh - 64px)', // Altura total menos a navbar
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: '1px solid #e2e8f0' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Menu
          </Typography>
          <Tooltip title="Recolher menu">
            <IconButton 
              onClick={onClose}
              size="small"
              sx={{ 
                color: 'text.secondary',
                '&:hover': { 
                  bgcolor: 'action.hover',
                  color: 'text.primary'
                }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

                           {/* Navigation Menu */}
        <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          <List sx={{ p: 2, flex: 1 }}>
           {/* Menu Items Principais */}
           {menuItems.map((item) => {
             // Filtrar itens baseado no role do usuário
             if (item.adminOnly && !user?.isAdmin) {
               return null;
             }

             // Para usuários não-admin, mostrar apenas "Minhas Assinaturas"
             if (!user?.isAdmin && item.text !== 'Minhas Assinaturas') {
               return null;
             }

             const active = isActive(item.path);
             
             return (
                                       <ListItem key={item.text} disablePadding disableGutters sx={{ mb: 1 }}>
                 <ListItemButton
                   onClick={() => handleNavigation(item.path)}
                   selected={active}
                   disableRipple
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                               sx={{
                           borderRadius: 2,
                           py: 1.5,
                           px: 2,
                           border: 'none',
                           outline: 'none',
                           '&.Mui-selected': {
                             backgroundColor: 'primary.main',
                             color: 'white',
                             '&:hover': {
                               backgroundColor: 'primary.dark',
                             },
                             '& .MuiListItemIcon-root': {
                               color: 'white',
                             },
                           },
                           '&:hover': {
                             backgroundColor: active ? 'primary.dark' : 'action.hover',
                           },
                           '&:focus': {
                             outline: 'none',
                             border: 'none',
                           },
                           '&:focus-visible': {
                             outline: 'none',
                             border: 'none',
                           },
                           '& .MuiTouchRipple-root': {
                             display: 'none',
                           },
                         }}
                 >
                   <ListItemIcon
                     sx={{
                       minWidth: 40,
                       color: active ? 'white' : 'text.secondary',
                     }}
                   >
                     {item.icon}
                   </ListItemIcon>
                   <ListItemText
                     primary={
                       <Typography 
                         variant="body1" 
                         sx={{ 
                           fontWeight: active ? 600 : 500,
                           fontSize: '0.95rem'
                         }}
                       >
                         {item.text}
                       </Typography>
                     }
                   />
                 </ListItemButton>
               </ListItem>
             );
           })}

           {/* Seção Configurações */}
           {user?.isAdmin && (
             <>
               <Divider sx={{ my: 2 }} />
               
               {/* Botão Configurações */}
                               <ListItem disablePadding disableGutters sx={{ mb: 1 }}>
                 <ListItemButton
                   onClick={handleSettingsToggle}
                   disableRipple
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                               sx={{
                           borderRadius: 2,
                           py: 1.5,
                           px: 2,
                           border: 'none',
                           outline: 'none',
                           '&:hover': {
                             backgroundColor: 'action.hover',
                           },
                           '&:focus': {
                             outline: 'none',
                             border: 'none',
                           },
                           '&:focus-visible': {
                             outline: 'none',
                             border: 'none',
                           },
                           '& .MuiTouchRipple-root': {
                             display: 'none',
                           },
                         }}
                 >
                   <ListItemIcon
                     sx={{
                       minWidth: 40,
                       color: 'text.secondary',
                     }}
                   >
                     <SettingsIcon />
                   </ListItemIcon>
                   <ListItemText
                     primary={
                       <Typography 
                         variant="body1" 
                         sx={{ 
                           fontWeight: 500,
                           fontSize: '0.95rem'
                         }}
                       >
                         Configurações
                       </Typography>
                     }
                   />
                   {settingsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                 </ListItemButton>
               </ListItem>

               {/* Submenu Configurações */}
               <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
                 <List component="div" disablePadding>
                   {settingsItems.map((item) => {
                     const active = isActive(item.path);
                     
                     return (
                       <ListItem key={item.text} disablePadding disableGutters sx={{ mb: 1 }}>
                         <ListItemButton
                           onClick={() => handleNavigation(item.path)}
                           selected={active}
                           disableRipple
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                               sx={{
                                   borderRadius: 2,
                                   py: 1.5,
                                   px: 2,
                                   ml: 2,
                                   border: 'none',
                                   outline: 'none',
                                   '&.Mui-selected': {
                                     backgroundColor: 'primary.main',
                                     color: 'white',
                                     '&:hover': {
                                       backgroundColor: 'primary.dark',
                                     },
                                     '& .MuiListItemIcon-root': {
                                       color: 'white',
                                     },
                                   },
                                   '&:hover': {
                                     backgroundColor: active ? 'primary.dark' : 'action.hover',
                                   },
                                   '&:focus': {
                                     outline: 'none',
                                     border: 'none',
                                   },
                                   '&:focus-visible': {
                                     outline: 'none',
                                     border: 'none',
                                   },
                                   '& .MuiTouchRipple-root': {
                                     display: 'none',
                                   },
                                 }}
                         >
                           <ListItemIcon
                             sx={{
                               minWidth: 40,
                               color: active ? 'white' : 'text.secondary',
                             }}
                           >
                             {item.icon}
                           </ListItemIcon>
                           <ListItemText
                             primary={
                               <Typography 
                                 variant="body1" 
                                 sx={{ 
                                   fontWeight: active ? 600 : 500,
                                   fontSize: '0.9rem'
                                 }}
                               >
                                 {item.text}
                               </Typography>
                             }
                           />
                         </ListItemButton>
                       </ListItem>
                     );
                   })}
                 </List>
               </Collapse>
             </>
           )}
         </List>
       </Box>

             {/* Footer */}
       <Box sx={{ p: 3, borderTop: '1px solid #e2e8f0', flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary" align="center" display="block">
          UniSign v1.0
        </Typography>
        <Typography variant="caption" color="text.secondary" align="center" display="block">
          © 2025 Todos os direitos reservados
        </Typography>
      </Box>
    </Drawer>
    </>
  );
};

export default Sidebar;
